<template>
    <component :is="isMainContent ? AppContent : 'div'" :breadcrumb="breadcrumb" :class="isMainContent ? 'module-shift-schedule' : ''" :headline="headline" class="module-shift-schedule">
        <template v-if="isMainContent && entry && entry.id" class="col-lg-12 col-xl-3" #sidebar>
            <portuna-box title="Weitere Informationen" title-icon="info" title-type="light">
                <strong class="color-contrast-primary-light">Zuletzt bearbeitet</strong>
                <entry-history
                    :history="entry"
                    module="shift-schedule"
                    namespace="work-group"
                    :entry-id="entry.id"
                    class="spacer-15"/>
            </portuna-box>
        </template>
        <loading-indicator v-if="loading"/>
        <div v-else>
            <form @submit.prevent="saveEntry" ref="form">
                <form-steps ref="formSteps"
                    #default="{isFirst, isLast, previousStep, nextStep, isComplete}"
                    tab-bar-group="work-group-edit"
                    use-steps>
                    <tab-bar-labels group="work-group-edit">
                        <tab-bar-label :use-step="1" group="work-group-edit" name="work-group" class="equal-label-width">
                            <span>Arbeitsbereich anlegen</span>
                        </tab-bar-label>

                        <tab-bar-label :use-step="2" group="work-group-edit" name="role" class="equal-label-width">
                            <span>Rolle anlegen</span>
                        </tab-bar-label>

                        <tab-bar-label :use-step="3" group="work-group-edit" name="options" class="equal-label-width">
                            <span>Optionen</span>
                        </tab-bar-label>
                    </tab-bar-labels>

                    <tab-bar-content name="work-group" group="work-group-edit" hide>
                        <div v-if="isAssistant" class="text-center default-margin-bottom">
                            <div class="assistant-wrapper-tab-disclaimer-headline text-center color-contrast-primary-light">
                                <strong>Arbeitsbereich anlegen</strong>
                            </div>
                            <div class="assistant-wrapper-tab-disclaimer-subtitle text-grey text-center">
                                Legen Sie zunächst einen Arbeitsbereich an. Später können Sie beliebig viele weitere Arbeitsbereiche ergänzen.
                                <br/>
                                Arbeitsbereiche stellen Organisationseinheiten wie z.B. Abteilungen, Stationen oder Arbeitsgruppen dar.
                                <br/>
                                Zu jedem Arbeitsbereich lassen sich Mitarbeiter zuordnen und Verantwortliche für die Schichtplanung festlegen
                            </div>
                        </div>
                        <component :is="isAssistant ? 'div' : 'portuna-box'" :class="{'xxl-padding': !isAssistant, 'assistant-content-border' : isAssistant}" :title="id ? 'Arbeitsbereich ' + entry.name + ' bearbeiten' : 'Arbeitsbereich anlegen'" title-icon="table_chart">
                            <form-row error-name="name" filter-name="name" label="Arbeitsbereich" label-for="name" help="Arbeitsbereiche stellen Organisationseinheiten wie z.B. Abteilungen, Stationen oder Arbeitsgruppen dar.">
                                <form-input v-model="entry.name" name="name" placeholder="Arbeitsbereichname"/>
                            </form-row>

                            <form-row error-name="employee_ids" label="Mitarbeiter auswählen" label-for="employees_ids">
                                <form-select-employee
                                    v-model="entry.employee_ids"
                                    :options="selects.employees"
                                    :remove-callback-promise="removeEmployee"
                                    multiple
                                    name="employee_ids[]"/>
                            </form-row>

                            <form-row error-name="responsible_employee_ids" label="Verantwortliche(r) für die Schichtplanung auswählen"
                                label-for="responsible_employee_ids">
                                <form-select-employee
                                    :class="{disabled: !entry.allowed_full_edit}"
                                    :disabled="!entry.allowed_full_edit"
                                    v-model="entry.responsible_employee_ids"
                                    :options="selects.employees"
                                    multiple
                                    name="responsible_employee_ids[]"/>

                                <input v-if="!entry.allowed_full_edit" v-model="entry.responsible_employee_ids" name="responsible_employee_ids[]" type="hidden">
                            </form-row>
                        </component>

                    </tab-bar-content>
                    <tab-bar-content name="role" group="work-group-edit" hide>
                        <div v-if="isAssistant" class="text-center default-margin-bottom">
                            <div class="assistant-wrapper-tab-disclaimer-headline text-center color-contrast-primary-light">
                                <strong>Rollen anlegen</strong>
                            </div>
                            <div class="assistant-wrapper-tab-disclaimer-subtitle text-grey text-center">
                                Legen Sie Rollen innerhalb des Arbeitsbereiches fest.
                                <br/>
                                Rollen entsprechen den im Arbeitsbereich anfallenden Aufgaben z.B. dem Telefon- Fahr- oder Empfangsdienst, der Rufbereitschaft, der Reinigung, dem technischen Support oder dem Verkauf.
                                <br/>
                                Später können Sie Schichten z.B. Früh-/Spätdienst für einzelne Rollen anlegen.
                            </div>
                        </div>
                        <component :is="isAssistant ? 'div' : 'portuna-box'" :class="{'xxl-padding': !isAssistant, 'assistant-content-border' : isAssistant}" title="Rollen bearbeiten" title-icon="table_chart">
                            <div class="row">
                                <div class="col-md-12">
                                    <form-row error-name="roles" label="Rolle" required help="Rollen entsprechen den im Arbeitsbereich anfallenden Aufgaben z.B. dem Telefon- Fahr- oder Empfangsdienst, der Rufbereitschaft, der Reinigung, dem technischen Support, Verkauf und vielen anderen mehr." help-alignment="top">
                                        <fieldset-cloneable v-model="entry.roles" :class="{disabled: !entry.allowed_full_edit}"
                                            :sortable="entry.allowed_full_edit">
                                            <template #render="{values, $index, manage}">
                                                <div class="container-fluid">
                                                    <div class="fieldset-row-padding flex flex-vertical-middle">
                                                        <div class="full-width">
                                                            <form-input
                                                                :id="'roles-' + $index + '-id'"
                                                                v-model="values.id"
                                                                :name="'roles[' + $index + '][id]'"
                                                                type="hidden"/>

                                                            <form-row
                                                                :error-name="'roles.' + $index + '.name'"
                                                                :label-for="'roles-' + $index + '-name'"
                                                                class="no-margin full-width" filter-name="roles.name">
                                                                <form-input v-model="values.name" :disabled="!entry.allowed_full_edit" :name="'roles[' + $index + '][name]'"
                                                                    placeholder="Rollenname"/>

                                                                <input v-if="!entry.allowed_full_edit" v-model="values.name" :name="'roles[' + $index + '][name]'"
                                                                    type="hidden">
                                                            </form-row>
                                                        </div>

                                                        <div v-if="entry.allowed_full_edit" class="fieldset-buttons full-height flex flex-vertical-middle flex-align-right">
                                                            <span v-interact="() => manage.clear($index)"
                                                                class="icon-interact"
                                                                aria-label="Leeren"
                                                                title="Leeren"
                                                                v-tooltip>
                                                                <i aria-hidden="true" class="material-icons text-warning" role="img">clear</i>
                                                            </span>

                                                            <span v-interact="() => isEditPage ? removeRole(manage, values, $index) : manage.delete($index)"
                                                                class="icon-interact"
                                                                aria-label="Löschen"
                                                                title="Löschen"
                                                                v-tooltip>
                                                                <i aria-hidden="true" class="material-icons text-danger" role="img">delete</i>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>

                                            <template v-if="entry.allowed_full_edit" #addButton="{add}">
                                                <div class="validate-spacer">
                                                    <div :class="{disabled: !entry.allowed_full_edit}"
                                                        class="button button-add" v-interact="add">Weitere Rollen hinzufügen
                                                    </div>
                                                </div>
                                            </template>
                                        </fieldset-cloneable>
                                    </form-row>
                                </div>
                            </div>
                        </component>

                    </tab-bar-content>
                    <tab-bar-content name="options" group="work-group-edit" hide>
                        <div v-if="isAssistant" class="text-center default-margin-bottom">
                            <div class="assistant-wrapper-tab-disclaimer-headline text-center color-contrast-primary-light">
                                <strong>Optionen</strong>
                            </div>
                            <div class="assistant-wrapper-tab-disclaimer-subtitle text-grey text-center">
                                Legen Sie bei Bedarf weitere Einstellungen für den Arbeitsbereich fest.
                                <br/>
                                Sie können diese Einstellungen jederzeit später ändern.
                            </div>
                        </div>
                        <component :is="isAssistant ? 'div' : 'portuna-box'" :class="{'xxl-padding': !isAssistant, 'assistant-content-border' : isAssistant}" title="Optionen bearbeiten" title-icon="table_chart">
                            <form-row error-name="ignore_multi_assign" label="Mehrfachbelegung ignorieren" label-for="ignore_multi_assign" hide-validator-icon>
                                <label class="pointer flex">
                                    <span>Belegungen von Schichten aus anderen Arbeitsbereichen werden ignoriert.</span>
                                    <form-input-checkbox v-model="entry.ignore_multi_assign" name="ignore_multi_assign" class="align-item-right"/>
                                </label>
                            </form-row>

                            <form-row error-name="notify_missed_shift" label="Benachrichtigung bei verpassten Schichten" label-for="notify_missed_shift"
                                hide-validator-icon>
                                <label class="pointer flex full-width">
                                    <span>Vorgesetzte bekommen eine Benachrichtigung, wenn Mitarbeiter nicht anwesend sind und eine Schicht beginnt.</span>
                                    <form-input-checkbox v-model="entry.notify_missed_shift" name="notify_missed_shift" class="align-item-right"/>
                                </label>
                            </form-row>

                            <form-row error-name="notify_premature_shift_logout" label="Benachrichtigung bei vorzeitigem Logout während laufender Schichten" label-for="notify_premature_shift_logout"
                                hide-validator-icon>
                                <label class="pointer flex full-width">
                                    <span>Vorgesetzte bekommen eine Benachrichtigung, wenn Mitarbeiter sich vor dem Ende zugewiesener Schichten von der Zeiterfassung ausloggt.</span>
                                    <form-input-checkbox v-model="entry.notify_premature_shift_logout" name="notify_premature_shift_logout" class="align-item-right"/>
                                </label>
                            </form-row>
                        </component>
                    </tab-bar-content>

                    <hr class="form-spacer no-margin-bottom"/>

                    <div class="flex flex-space-between button-container">
                        <div class="button button-cancel" v-interact="redirectToHome">
                            <i class="material-icons" aria-hidden="true" role="img">clear</i>
                            <span>Abbrechen</span>
                        </div>
                        <div class="flex-align-right flex">
                            <div v-if="!isFirst" class="button button-back" v-interact="previousStep">Zurück</div>
                            <template v-if="isLast">
                                <div v-if="!isAssistant && id" class="button button-danger" v-interact="deleteEntry">
                                    <i class="material-icons md-16" aria-hidden="true" role="img">delete</i>Löschen
                                </div>
                                <div v-if="isLast" class="button button-save" v-interact="saveEntry">
                                    <i aria-hidden="true" class="material-icons" role="img">save</i>Speichern
                                </div>
                            </template>
                            <div v-else class="button button-next" v-interact="nextStep" :class="{disabled: !isValid && !isComplete}">Weiter</div>
                        </div>
                    </div>
                </form-steps>
            </form>
        </div>
    </component>
</template>

<script setup lang="ts">
    import {useRouterUtils} from '@/composables/routerUtils';
    import {usePortuna} from '@/composables/portuna';
    import axios, {AxiosError, type AxiosResponse} from 'axios';
    import EntryHistory from '@/components/EntryHistory.vue';
    import {formSubmit, getFormData, parseFormData} from '@/library/form';
    import {getEmployeeBasicBreadcrumb} from '@/library/modules/employee/employee';
    import {Popup} from '@/library/popup';
    import {applyChangesPrompt, defineAsyncComponentWrapper} from '@/library/utils';
    import {errorHandler, sendPopupRequest, XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import FieldsetCloneable from '../../components/FieldsetCloneable.vue';
    import FormInput from '../../components/form/FormInput.vue';
    import FormInputCheckbox from '../../components/form/FormInputCheckbox.vue';
    import FormRow from '../../components/form/FormRow.vue';
    import FormSelectEmployee from '../../components/form/FormSelectEmployee.vue';
    import FormSteps from '../../components/form/FormSteps.vue';
    import LoadingIndicator from '../../components/LoadingIndicator.vue';
    import PortunaBox from '../../components/PortunaBox.vue';
    import TabBarContent from '../../components/TabBarContent.vue';
    import TabBarLabel from '../../components/TabBarLabel.vue';
    import TabBarLabels from '../../components/TabBarLabels.vue';
    import TopMessageHandler from '../../library/topMessageHandler';
    import dateHelper from '../../utils/portuna/dateHelper';
    import {computed, ref, watch} from 'vue';
    import {useStore} from 'vuex';
    import {useRouter} from 'vue-router';

    const AppContent = defineAsyncComponentWrapper(() => import('@/components/AppContent.vue'));

    const portuna     = usePortuna();
    const routerUtils = useRouterUtils();

    const emit = defineEmits<{
        submit: [];
        proceed: [];
        close: [];
    }>();

    const props = withDefaults(
        defineProps<{
            id?: Nullable<number>;
            isMainContent?: boolean;
            isAssistant: boolean;
        }>(),
        {
            id: null,
            isMainContent: true,
            isAssistant: false,
        },
    );

    const store  = useStore();
    const router = useRouter();

    const breadcrumb = computed((): Breadcrumb[] =>
    {
        return [
            getEmployeeBasicBreadcrumb(),
            {
                label: 'Schichtplanung',
                href: {name: 'shift-schedule-calendar'},
            },
        ];
    });

    const form      = ref<HTMLFormElement>();
    const formSteps = ref<FormSteps>();

    const errorPage = async (error: AxiosError | AxiosResponse): Promise<void> => store.dispatch('errorPage', error);

    const viewName = ref<string>('WorkGroupEdit');
    const loading  = ref<boolean>(true);
    const entry    = ref<Nullable<WorkGroupEntry>>(null);
    const selects  = ref<Record<'employees', SelectOption[]>>({
        employees: [],
    });
    const headline = ref<string>('Arbeitsbereich anlegen');

    const removeRole = (manage: any, values: any, index: number): void =>
    {
        if ('undefined' === typeof values.id)
        {
            manage.delete(index);
            return;
        }

        let popup: Popup = new Popup({
            title: 'Rolle löschen',
            titleIcon: 'delete',
            text: `Sind Sie sicher, dass Sie die Rolle <strong>${values.name}</strong> löschen wollen? <strong class="text-danger">Achtung</strong> die Rolle und alle dazugehörigen Schichten werden vollständig aus dem Arbeitsbereich gelöscht.`,
            textIcon: 'delete_forever',
            textIconClass: 'text-danger',
            size: 'small',
            buttons: [
                {
                    title: 'Abbrechen',
                    icon: 'clear',
                    cmd: () => popup.close(),
                },
                {
                    title: 'Löschen',
                    icon: 'delete',
                    type: 'danger',
                    cmd: () =>
                    {
                        manage.delete(index);
                        popup.close();
                    },
                },
            ],
        });

        popup.open();
    };

    const isEditPage = computed((): boolean =>
    {
        return props.isMainContent && !!props.id;
    });

    const isCreatePage = computed((): boolean =>
    {
        return props.isMainContent && !props.id;
    });

    const isValid = computed(() =>
    {
        return entry.value && (entry.value.employee_ids as any[])?.length && entry.value.name;
    });

    const watchEntry = (): void =>
    {
        if (formSteps.value)
        {
            formSteps.value.checkFormCompletion();
        }
    };

    const fetchData = (): void =>
    {
        loading.value = true;
        axios
        .get(
            '/employee/shift-schedule/work-group/edit/id/' +
            props.id +
            '/date/' +
            dateHelper('Y-m-d') +
            '/is_assistant/' +
            (props.isAssistant ? 1 : 0),
        )
        .then((response) =>
        {
            const {
                      data,
                      status,
                  } = response.data;

            if (XHR_STATUS_DEFAULT !== status)
            {
                throw response;
            }

            store.dispatch('form/setFilter', data.filter);
            entry.value = data.entry;
            selects.value     = data.selects;

            if (props.id && entry.value)
            {
                headline.value = 'Arbeitsbereich "' + entry.value.name + '" bearbeiten';
            }
        })
        .catch(errorPage)
        .finally(() =>
        {
            loading.value = false;
        });
    };

    const deleteEntry = (): void =>
    {
        sendPopupRequest({
            url: '/employee/shift-schedule/work-group/delete/id/' + entry.value?.id,
            message: {
                headline: 'Löschen',
                message:
                    'Sind Sie sicher, dass Sie den Arbeitsbereich <strong>' +
                    entry.value?.name +
                    '<' +
                    '/strong> löschen möchten?',
            },
            options: {
                textIcon: 'delete_forever',
                textIconClass: 'text-danger',
                titleIcon: 'delete',
                buttonOkay: {
                    type: 'danger',
                    icon: 'delete',
                    title: 'Löschen',
                },
            },
        })
        .catch(errorHandler)
        .then(() => router.push({name: 'shift-schedule-calendar'}));
    };

    const removeEmployee = (person: User): Promise<void> =>
    {
        return new Promise((resolve, reject) =>
        {
            if (isCreatePage.value || props.isAssistant)
            {
                return resolve();
            }

            let popup = new Popup({
                title: 'Löschen',
                titleIcon: 'delete',
                text: `Sind Sie sicher, dass Sie <strong>${person.name}</strong> ab dem <strong>${dateHelper('d.m.Y')}</strong>, aus dem Arbeitsbereich <strong>${entry.value?.name}</strong> löschen möchten?`,
                textIcon: 'delete_forever',
                textIconClass: 'text-danger',
                size: 'small',
                buttons: [
                    {
                        title: 'Löschen',
                        icon: 'delete',
                        type: 'danger',
                        cmd: () =>
                        {
                            popup.close();
                            resolve();
                        },
                    },
                    {
                        title: 'Abbrechen',
                        icon: 'clear',
                        cmd()
                        {
                            popup.close();
                            reject();
                        },
                    },
                ],
            });

            popup.open();
        });
    };

    const proceed = () =>
    {
        emit('proceed');
        portuna.emitter.emit('proceed');
    };

    const redirectToHome = () =>
    {
        if (props.isAssistant)
        {
            applyChangesPrompt(
                `Möchten Sie die Änderungen anwenden oder <strong>jegliche</strong> Einstellungen der Schichtplanung verwerfen?`,
            )
            .then(() =>
            {
                const formData = getFormData(form.value!);
                return formSubmit(
                    '/employee/shift-schedule/work-group/save-incomplete/id/' + props.id,
                    {},
                    parseFormData(formData),
                )
                .then(() =>
                    TopMessageHandler.success(
                        'Die Änderungen wurden erfolgreich gespeichert. Sie können den Einrichtungsassistenten jederzeit wieder öffnen.',
                    ),
                )
                .catch(() => TopMessageHandler.error('Die Änderungen konnten nicht gespeichert werden.'));
            })
            .catch(() =>
            {
                axios.post('/employee/shift-schedule/work-group/reset-all')
                .then(() =>
                    TopMessageHandler.success(
                        'Die Änderungen wurden erfolgreich verworfen. Sie können den Einrichtungsassistenten jederzeit wieder öffnen.',
                    ),
                )
                .catch(() => TopMessageHandler.error('Die Änderungen konnten nicht verworfen werden.'));
            })
            .finally(() =>
            {
                emit('close');
                router.push({name: 'home'});
            });
        }
        else
        {
            routerUtils.routerGoBack();
        }
    };

    const watchId = (): void =>
    {
        fetchData();
    };

    fetchData();

    const save = (formSteps: Nullable<FormSteps>, additionallyData?: [] | {}): Promise<any> =>
    {
        return new Promise((resolve, reject) =>
        {
            const formData = getFormData(form.value!, additionallyData);

            return formSubmit(
                '/employee/shift-schedule/work-group/edit/id/' +
                props.id +
                '/date/' +
                dateHelper('Y-m-d') +
                '/is_assistant/' +
                (props.isAssistant ? 1 : 0),
                {formSteps},
                parseFormData(formData),
            )
            .then((data: any) =>
            {
                TopMessageHandler.success(data.message);
                resolve(data);

                if (props.isAssistant)
                {
                    proceed();
                }
            })
            .catch((error) =>
            {
                errorHandler(error);
                reject(error);
            });
        });
    };

    const saveEntry = (): void =>
    {
        save(null).then(() =>
        {
            if (!props.isAssistant)
            {
                router.push({name: 'shift-schedule-calendar'});
            }
            else
            {
                emit('submit');
            }
        });
    };

    watch(() => props.id, watchId);
    watch(entry, watchEntry, {deep: true});

    defineExpose(Object.assign({viewName, save}, portuna));
</script>